import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "build-ts/metadata";
import { buildWorkflowTag } from "modules/chat/utils/chatUtils";
import { ScheduledJobProto, ScheduledScenarioJobProto } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import {
  setScheduledJobIsActive,
  deleteScheduledJob,
  getScheduledJob,
  putScheduledJob,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import ConfirmationModal from "common/modals/ConfirmationModal";
import JobList from "modules/analyst/components/JobList";

interface JobListContainerProps {
  jobs: Header[];
  isLoadingJobs: boolean;
  onJobClick?: (job: Header) => void;
  onRefresh?: () => void;
}

const JobListContainer: React.FC<JobListContainerProps> = ({ jobs, isLoadingJobs, onJobClick, onRefresh }) => {
  const navigate = useNavigate();
  const { userId } = useUser();
  const [copyingJobId, setCopyingJobId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Header | null>(null);

  const handleToggleActive = async (job: Header, isActive: boolean) => {
    if (!job.id) return;
    const response = await setScheduledJobIsActive(job.id, isActive);
    if (response) {
      showToast(
        isActive ? "Workflow set to Live" : "Workflow paused",
        `"${job.name}" is now ${isActive ? "live" : "paused"}.`,
        "success"
      );
      onRefresh?.();
    }
  };

  const handleRunJob = async (job: Header) => {
    // Job details are needed to get the darwin name because LLM needs the darwin name to run the
    // workflow.
    const jobDetails = await getScheduledJob(job.id!, false);
    if (!jobDetails) {
      return;
    }
    navigate("/chat/new", {
      state: {
        initialPrompt:
          "I want to run the workflow:" +
          `${buildWorkflowTag(jobDetails.scheduled_analysis?.darwin_name!, job.name!)}.` +
          " Fetch the details of the workflow and run it. Ask me If there is any input required to run the workflow.",
        analystId: job.owner_id,
      },
    });
  };

  const handleCopyJob = async (job: Header) => {
    if (!job.id) return;
    setCopyingJobId(job.id);
    try {
      const jobDetails = await getScheduledJob(job.id, false);
      if (!jobDetails) {
        showToast("Failed to fetch workflow details", "", "error");
        return;
      }

      const currentDarwinName = jobDetails.scheduled_analysis?.darwin_name?.trim() || "";
      const randomSuffix = Math.random().toString(36).slice(2, 8);
      const duplicatedScheduledAnalysis = ScheduledScenarioJobProto.create({
        ...jobDetails.scheduled_analysis,
        darwin_name: `${currentDarwinName}_${randomSuffix}`,
      });

      const newJob = ScheduledJobProto.create({
        header: Header.create({
          id: uuidv4(),
          name: `${jobDetails.header?.name || "Untitled"} (Copy)`,
          type: MetadataTypeEnum.SCHEDULED_JOB,
          owner_id: jobDetails.header?.owner_id,
        }),
        cron_expr: jobDetails.cron_expr,
        type: jobDetails.type,
        is_active: false,
        scheduled_analysis: duplicatedScheduledAnalysis,
      });

      const response = await putScheduledJob(newJob);
      if (response) {
        showToast("Workflow duplicated", `"${newJob.header?.name}" has been created.`, "success");
        onRefresh?.();
      } else {
        showToast("Failed to duplicate", "", "error");
      }
    } catch {
      showToast("Failed to duplicate", "", "error");
    } finally {
      setCopyingJobId(null);
    }
  };

  const handleDeleteClick = (job: Header) => {
    setJobToDelete(job);
  };

  const handleDeleteCancel = () => {
    setJobToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete?.id) return;
    setIsDeleting(true);
    const response = await deleteScheduledJob(jobToDelete.id);
    setIsDeleting(false);
    if (response) {
      showToast("Workflow deleted", `"${jobToDelete.name}" has been deleted.`, "success");
      setJobToDelete(null);
      onRefresh?.();
    }
  };

  return (
    <>
      <JobList
        jobs={jobs}
        isLoadingJobs={isLoadingJobs}
        userId={userId}
        copyingJobId={copyingJobId}
        isDeleting={isDeleting}
        onJobClick={onJobClick}
        onToggleActive={handleToggleActive}
        onRunJob={handleRunJob}
        onCopyJob={handleCopyJob}
        onDeleteClick={handleDeleteClick}
      />

      <ConfirmationModal
        isOpen={!!jobToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Workflow"
        text={`Are you sure you want to delete "${jobToDelete?.name ?? ""}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </>
  );
};

export default JobListContainer;
